import React, { useState, useMemo } from 'react';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

function FunctionSelector({
  title = 'Function Filter',
  functions,
  selectedFunctions,
  onFunctionToggle,
  onToggleAll,
  loading,
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFunctions = useMemo(() => {
    if (!functions) return [];
    if (!searchQuery.trim()) return functions;
    const q = searchQuery.toLowerCase();
    return functions.filter((f) => f.name.toLowerCase().includes(q));
  }, [functions, searchQuery]);

  if (loading) {
    return (
      <div className="card p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded"></div>
            <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!functions || functions.length === 0) {
    return null;
  }

  const allSelected = selectedFunctions.length === functions.length;
  const totalRequirements = functions
    .filter((f) => selectedFunctions.includes(f.name))
    .reduce((sum, f) => sum + f.requirement_count, 0);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
          {title}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleAll(true)}
            disabled={allSelected}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              allSelected
                ? 'text-neutral-400 dark:text-neutral-600 cursor-not-allowed'
                : 'text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
            }`}
          >
            Select All
          </button>
          <span className="text-neutral-300 dark:text-neutral-700">|</span>
          <button
            onClick={() => onToggleAll(false)}
            disabled={selectedFunctions.length === 0}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              selectedFunctions.length === 0
                ? 'text-neutral-400 dark:text-neutral-600 cursor-not-allowed'
                : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
            }`}
          >
            Deselect All
          </button>
        </div>
      </div>

      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
        {selectedFunctions.length} of {functions.length} selected ({totalRequirements} requirements)
      </p>

      {functions.length > 5 && (
        <div className="relative mb-3">
          <MagnifyingGlassIcon className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}...`}
            className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      )}

      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {filteredFunctions.length === 0 && searchQuery.trim() && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">
            No matches for "{searchQuery}"
          </p>
        )}
        {filteredFunctions.map((func) => {
          const isSelected = selectedFunctions.includes(func.name);

          return (
            <div
              key={func.name}
              className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                isSelected
                  ? 'border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/20'
                  : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900'
              }`}
              onClick={() => onFunctionToggle(func.name)}
            >
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5 transition-colors ${
                  isSelected
                    ? 'bg-primary-600 border-primary-600'
                    : 'border-neutral-300 dark:border-neutral-600'
                }`}
              >
                {isSelected && (
                  <CheckCircleIcon className="w-4 h-4 text-white" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 dark:text-white break-words">
                  {func.name}
                </p>
              </div>

              <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-2 flex-shrink-0 mt-0.5">
                {func.requirement_count} req{func.requirement_count !== 1 ? 's' : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FunctionSelector;
